import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState, useMemo} from "react";
import {retainActions} from "../../../../../../_actions";
import {RetainCampaignBuilder} from "../objects/RetainCampaignBuilder";
import _ from "lodash";
import {formatProactiveMetric} from "../../../Proactive/Utilities/CampaignUtils";
import {buildMetricsDetailsUrl, formatCurrency, formatRevenue, RETAIN_CAMPAIGNS} from "../CampaignUtils";
import moment from "moment";
import CampaignActions from "components/v2/views/ProactiveMetricsDetail/Utilities/CampaignActions";

export default function (customParams) {
    const dispatch = useDispatch();
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;
    const { organization_id = '' } = JSON.parse(localStorage.user);
    const params = {
        startDate: customParams?.startDate || moment().subtract(2, 'years').startOf('day').toISOString(),
        endDate: customParams?.endDate || moment().endOf('day').toISOString(), // Current date, end of day
    };

    useEffect(() => {
        if(customParams) {
            if(customParams.organizationId){
                dispatch(retainActions.getOrganizationRetainCampaigns(customParams.organizationId, params));
            }
        } else {
            dispatch(retainActions.getCompanyRetainCampaigns())
        }
    }, [customParams?.refreshTrigger]);

    const retainCampaigns = useSelector(state => state.retain);
    const loading = retainCampaigns.loadingRetainCampaign;
    const campaignsObj = retainCampaigns.retainCampaigns;
    const campaignList = _.get(campaignsObj, 'items') || [];
    const totalCount = _.get(campaignsObj, 'totalCount') || 0;
    const rows = [];

    if(!loading && campaignList.length > 0) {
        campaignList.map( item => {
            const {campaign, metrics} = item;
            const builder = new RetainCampaignBuilder()

            builder.setId(_.get(campaign, 'id') || '-');
            builder.setName(_.get(campaign, 'name') || '-');
            builder.setConversations(_.get(metrics, 'conversations') || '-');
            builder.setWinbackRevenue(formatRevenue(_.get(metrics, 'winbackRevenue.amount')));
            builder.setRevenuePerRecipient(formatCurrency(_.get(metrics, 'revenuePerRecipient.amount')));
            builder.setAverageWinbackOrderValue(formatCurrency(_.get(metrics, 'averageWinbackOrderValue.amount')));
            builder.setConversionRate(_.get(metrics, 'conversionRate') || '-');
            builder.setConversions(_.get(metrics, 'conversions') || '-');
            builder.setOrders(_.get(metrics, 'orders') || '-');
            builder.setAdditionalRevenue(formatCurrency(_.get(metrics, 'additionalRevenue.amount')));
            builder.setLtvIncreaseRatio(_.get(metrics, 'ltvIncreaseRatio') || '-');
            builder.setAdditionalOrdersRate(_.get(metrics, 'additionalOrdersRate') || '-');
            if(customParams) builder.setActions(<CampaignActions campaign={builder.campaign} />)

            builder.setParam(buildMetricsDetailsUrl({
                id: _.get(campaign, 'id'),
                source: _.get(campaign, 'source'),
                organizationId:customParams ? customParams.organizationId : organization_id
                }, RETAIN_CAMPAIGNS, params))
            ;

            rows.push(builder.campaign);
        })
    }

    const paginatedRows = useMemo(() => {
        return rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [rows, page]);

    const changePage = (nextPage) => {
        setPage(nextPage)
    }

    const smallWidth = 30;

    const baseColumns = [
        {
            id: 'name',
            label: 'Campaign',
            width: 150,
            clickable: true,
            isSortable: true,
            withTooltip: true,
        },
        {
            id: 'conversations',
            label: 'Recipients',
            width: smallWidth,
            isSortable: true,
            align: 'right',
            format: (value) => value.toLocaleString('en-US'),
            withHeaderTooltip: true,
            headerTooltipText: 'Total customers who received Retain AI outreach for this campaign.'
        },
        {
            id: 'winbackRevenue',
            label: 'Winback Rev.',
            width: smallWidth,
            isSortable: true,
            align: 'right',
            withHeaderTooltip: true,
            headerTooltipText: 'Total revenue from the first order placed by successfully won-back customers.'
        },
        {
            id: 'revenuePerRecipient',
            label: 'RPR',
            width: smallWidth,
            clickable: true,
            isSortable: true,
            align: 'right',
            withHeaderTooltip: true,
            headerTooltipText: 'Revenue Per Recipient (RPR): Winback Revenue divided by the Total Recipients contacted.'
        },
        {
            id: 'averageWinbackOrderValue',
            label: 'AOV',
            width: smallWidth,
            isSortable: true,
            align: 'right',
            withHeaderTooltip: true,
            headerTooltipText: 'Average order value from winback orders: winback revenue divided by number of winbacks.'
        },
        {
            id: 'conversionRate',
            label: 'Conversion',
            width: smallWidth,
            isSortable: true,
            align: 'right',
            format: formatProactiveMetric,
            withHeaderTooltip: true,
            headerTooltipText: 'Winback conversion rate: winbacks divided by total recipients.'
        },
        {
            id: 'conversions',
            label: 'Winbacks',
            width: smallWidth,
            isSortable: true,
            align: 'right',
            withHeaderTooltip: true,
            headerTooltipText: 'Number of customers who placed an order within 30 days of receiving Retain AI outreach.'
        },
        {
            id: 'additionalOrdersRate',
            label: 'Repeat',
            width: smallWidth,
            isSortable: true,
            align: 'right',
            format: formatProactiveMetric,
            withHeaderTooltip: true,
            headerTooltipText: 'The percentage of won-back customers who placed a subsequent order.'
        },
        {
            id: 'additionalRevenue',
            label: 'Add. Rev.',
            width: smallWidth,
            isSortable: true,
            align: 'right',
            withHeaderTooltip: true,
            headerTooltipText: 'Total revenue from all subsequent orders placed after the initial winback order.'
        },
        {
            id: 'ltvIncreaseRatio',
            label: 'LTV Incr.',
            width: smallWidth,
            isSortable: true,
            align: 'right',
            format: formatProactiveMetric,
            withHeaderTooltip: true,
            headerTooltipText:  (
                <>
                    Increase in lifetime value for customers who were won back - {" "}
                    <a
                        href="https://help.beremarkable.ai/en/articles/12168626-retain-ai-dashboard-metrics"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Learn more
                    </a>
                </>
            )
        },
    ];
    const columns = !customParams ? baseColumns : [
        ...baseColumns,
        {
            id: 'actions',
            label: 'Actions',
            width: smallWidth,
            align: 'right',
        }
    ];

    return { rows: paginatedRows, columns, totalCount, loading, changePage, page };
}
